import { useEffect, useMemo } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import { useDynamicPageStore } from "@companion-core/shared/app/Store/dynamicPageStore";
import { useDynamicPageStepperStore } from "@companion-core/shared/app/Store/dynamicPageStepperStore";
import { useBlocks } from "@companion-core/shared/app/Queries";
import {
  isBlocksContainFullBleedBlock,
  isBlocksContainVerticalList,
} from "@companion-core/shared/app/Utils/componentHelper";
import {
  containsJourneyBlock,
  isOnboardingEnabled,
} from "@companion-core/shared/app/Components/Blocks/Layouts/PostListJourneyLayout/postListJourneyLayout.utils";
import { BlockParams } from "@companion-core/shared/app/Interfaces/blocks";
import { transformPageBlocksWithTabContainer } from "@companion-core/shared/app/Components/DynamicPage/dynamicPage.model";
import { useDynamicPageParams } from "./dynamicPageHooks";

export const useDynamicPageContent = () => {
  const { locale: lang } = useI18nStore((state) => ({ locale: state.locale }));
  const { pageSlug, checkInternetConnection, needRefresh, redirectToNotFound } =
    useDynamicPageParams();
  const queryClient = useQueryClient();
  const { isPending, data } = useBlocks(pageSlug ?? "", lang);
  const sortedData = useMemo(() => [...(data ?? [])].sort((a, b) => a.order - b.order), [data]);
  const parsedData = useMemo(() => transformPageBlocksWithTabContainer(sortedData), [sortedData]);
  const onboardingEnabled = isOnboardingEnabled();

  useEffect(() => {
    if (needRefresh) {
      queryClient.invalidateQueries({ queryKey: ["blocks", pageSlug, lang] });
    }
  }, [needRefresh]);

  const ensureContentAvailable = () => {
    if (!isPending && !parsedData?.length) {
      checkInternetConnection();
      return false;
    }
    return true;
  };

  return {
    lang,
    pageSlug,
    isPending,
    data,
    sortedData,
    parsedData,
    onboardingEnabled,
    checkInternetConnection,
    redirectToNotFound,
    needRefresh,
    ensureContentAvailable,
  };
};

export const useDynamicPage = () => {
  const content = useDynamicPageContent();
  const { pageSlug, isPending, data, sortedData, parsedData, onboardingEnabled } = content;
  const { setPage, resetPage } = useDynamicPageStore();

  const pageContainsVerticalList = useMemo(() => isBlocksContainVerticalList(data ?? []), [data]);
  const pageContainsJourneyBlock = useMemo(
    () => containsJourneyBlock(parsedData ?? []),
    [parsedData]
  );
  const pageContainsFullBleedBlock = useMemo(
    () => isBlocksContainFullBleedBlock(parsedData ?? []),
    [parsedData]
  );
  const pageUsesFillLayout =
    pageContainsVerticalList ||
    pageContainsFullBleedBlock ||
    (onboardingEnabled && pageContainsJourneyBlock);
  const shouldRedirectToNotFound = !isPending && pageContainsJourneyBlock && !onboardingEnabled;

  useEffect(() => {
    if (data) {
      resetPage();
      setPage(pageSlug ?? "", sortedData);
    }
  }, [sortedData]);

  return { ...content, pageContainsVerticalList, pageUsesFillLayout, shouldRedirectToNotFound };
};

export const useDynamicPageStepper = () => {
  const content = useDynamicPageContent();
  const { pageSlug, isPending, data, sortedData, parsedData, onboardingEnabled } = content;
  const {
    pageSlug: currentPageSlug,
    pages,
    setStepIndex,
    setPageSlug,
    setBlocks,
    setBlockParams,
  } = useDynamicPageStepperStore();

  const storedStepIndex = currentPageSlug ? (pages[currentPageSlug]?.stepIndex ?? 0) : 0;
  const stepIndex = storedStepIndex < parsedData.length ? storedStepIndex : 0;
  const block = parsedData[stepIndex];
  const shouldRedirectToNotFound = !isPending && !onboardingEnabled;

  useEffect(() => {
    if (!pageSlug) return;
    setPageSlug(pageSlug);
    if (data) setBlocks(sortedData);
  }, [pageSlug, sortedData, setPageSlug, setBlocks]);

  const onGoToPrevious = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const onGoToNext = (params: BlockParams) => {
    if (stepIndex < parsedData.length - 1) setStepIndex(stepIndex + 1);
    setBlockParams(params, stepIndex + 1);
  };

  return {
    ...content,
    stepIndex,
    block,
    setStepIndex,
    onGoToPrevious,
    onGoToNext,
    shouldRedirectToNotFound,
  };
};
